import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Chip,
} from "@mui/material";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import DataTableLayout from "../../Common/UI/DataTableLayout";
import DataTableFooter from "../../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../../utils/dataTableShell";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import { DateTime } from "luxon";
import StatusBadge from "../../Common/UI/StatusTag";
import RedactionPreviewModal from "../Modals/RedactionPreviewModal";
import { PARTICIPANT_LOCKED_MESSAGE } from "../../../utils/participantLock";

const statusMap: any = {
  live: { label: "Live", variant: "active" },
  not_live: { label: "Not Live", variant: "cancelled" },
  closed: { label: "Closed", variant: "completed" },
};

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("MM/dd/yyyy") : null;

interface Props {
  /** When true, all per-row mutate actions are disabled. */
  isLocked?: boolean;
  /** Phase 6c — gates the per-row "Revive" action on redacted enrolments. */
  canStudyRevive?: boolean;
}

const ParticipantStudies: React.FC<Props> = ({ isLocked, canStudyRevive }) => {
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const { participantId } = useParams();
  const [showRedactModal, setShowRedactModal] = useState(false);
  const [showReviveModal, setShowReviveModal] = useState(false);
  const [targetStudyId, setTargetStudyId] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        let url = `/admin/registry/participants/${participantId}/studies`;

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const newData = data?.map((row: any) => ({
          id: row?.id,
          studyName: row?.name,
          status: row?.status,
          createdAt:
            row?.addedOn &&
            DateTime.fromISO(row.addedOn).toFormat("MM/dd/yyyy"),
          lastParticipationDate:
            row?.lastParticipationDate &&
            DateTime.fromISO(row.lastParticipationDate).toFormat("MM/dd/yyyy"),
          redactedAt: row?.redactedAt ?? null,
          consentWithdrawnAt: row?.consentWithdrawnAt ?? null,
        }));
        setData(newData || []);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchDetails();
  }, [participantId]);

  const resolvedCount = useMemo(
    () =>
      data.filter(
        (row: any) => row?.redactedAt || row?.consentWithdrawnAt,
      ).length,
    [data],
  );

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          !loading && data?.length > 0 ? (
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Enrolments resolved (redacted or consent withdrawn):
              </Typography>
              <Chip
                size="small"
                color={resolvedCount === data.length ? "success" : "warning"}
                label={`${resolvedCount} of ${data.length}`}
              />
              {resolvedCount < data.length && (
                <Typography variant="caption" color="text.secondary">
                  All enrolments must be resolved before Database Redact can run.
                </Typography>
              )}
            </Box>
          ) : undefined
        }
        footer={
          <DataTableFooter
            filteredCount={data.length}
            page={0}
            pageSize={data.length || 1}
            onPageChange={() => {}}
            loading={loading}
            hidePagination
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Study Name</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Creation Date</StyledTableCell>
              <StyledTableCell>Study Completion Date</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          {!loading && data?.length > 0 && (
            <TableBody>
              {data?.map((row: any, index: number) => (
                <TableRow key={index}>
                  <StyledTableCell>{row?.studyName || "-"}</StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge
                      label={statusMap[row?.status]?.label ?? row?.status}
                      variant={statusMap[row?.status]?.variant}
                    />
                  </StyledTableCell>
                  <StyledTableCell>{row?.createdAt || "-"}</StyledTableCell>
                  <StyledTableCell>
                    {row?.lastParticipationDate || "-"}
                  </StyledTableCell>
                  <StyledTableCell>
                    {row?.redactedAt ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          size="small"
                          color="error"
                          label={`Redacted ${
                            formatDate(row.redactedAt) ?? ""
                          }`.trim()}
                        />
                        {canStudyRevive && !isLocked && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => {
                              setTargetStudyId(row.id);
                              setShowReviveModal(true);
                            }}
                          >
                            Revive
                          </Button>
                        )}
                      </Box>
                    ) : row?.consentWithdrawnAt ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={`Consent withdrawn ${
                          formatDate(row.consentWithdrawnAt) ?? ""
                        }`.trim()}
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => { setTargetStudyId(row.id); setShowRedactModal(true); }}
                        disabled={isLocked}
                        title={isLocked ? PARTICIPANT_LOCKED_MESSAGE : undefined}
                      >
                        Redact Study Data
                      </Button>
                    )}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!loading && data?.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      {showRedactModal && targetStudyId && (
        <RedactionPreviewModal
          open={showRedactModal}
          onClose={() => { setShowRedactModal(false); setTargetStudyId(null); }}
          fetchPreview={() =>
            http.get(`/admin/participants/${participantId}/studies/${targetStudyId}/redact/preview`)
              .then((r: any) => r.data.data)
          }
          onConfirm={async (reason: string) => {
            await http.post(
              `/admin/participants/${participantId}/studies/${targetStudyId}/redact`,
              { reason },
            );
            setData((prev: any[]) =>
              prev.map((s) =>
                s.id === targetStudyId
                  ? { ...s, redactedAt: new Date().toISOString() }
                  : s,
              ),
            );
            setShowRedactModal(false);
            setTargetStudyId(null);
          }}
        />
      )}
      {/* Phase 6c — study-tier revive. Shares RedactionPreviewModal scaffold
          via mode='revive' so labels, action column, and CTA flip to the
          restoration semantics without duplicating the dialog shell. */}
      {showReviveModal && targetStudyId && (
        <RedactionPreviewModal
          open={showReviveModal}
          mode="revive"
          onClose={() => { setShowReviveModal(false); setTargetStudyId(null); }}
          fetchPreview={() =>
            http.get(`/admin/participants/${participantId}/studies/${targetStudyId}/revive/preview`)
              .then((r: any) => r.data.data)
          }
          onConfirm={async (reason: string) => {
            await http.post(
              `/admin/participants/${participantId}/studies/${targetStudyId}/revive`,
              { reason },
            );
            setData((prev: any[]) =>
              prev.map((s) =>
                s.id === targetStudyId ? { ...s, redactedAt: null } : s,
              ),
            );
            setShowReviveModal(false);
            setTargetStudyId(null);
          }}
        />
      )}
    </Box>
  );
};

export default ParticipantStudies;
